import type { ServerProviderSkill } from "@afar-research/contracts";
import { isUserFacingSkillBundleId } from "@afar-research/agent-content";
import {
  insertRankedSearchResult,
  normalizeSearchQuery,
  scoreQueryMatch,
} from "@afar-research/shared/searchRanking";

import { formatProviderSkillDisplayName } from "./providerSkillPresentation";

/**
 * The skills a person can pick by name. App-owned Skill Bundles stay out unless
 * they are user facing: the rest are invoked through Actions or by the agent.
 * Provider skills — project and user ones — are always offered.
 */
export function filterUserFacingProviderSkills(
  skills: ReadonlyArray<ServerProviderSkill>,
): ServerProviderSkill[] {
  return skills.filter(
    (skill) => skill.ownership !== "app" || isUserFacingSkillBundleId(skill.name),
  );
}

function providerSkillScopeRank(skill: ServerProviderSkill): number {
  if (skill.ownership === "app") return 1;
  const scope = skill.scope?.trim().toLowerCase();
  return scope === "repo" || scope === "project" || scope === "workspace" || scope === "local"
    ? 0
    : 2;
}

/**
 * Normalize one provider instance's current-project catalog at the client boundary.
 * The provider-reported canonical path is the identity; name is presentation only.
 */
export function normalizeProviderSkillCatalog(
  skills: ReadonlyArray<ServerProviderSkill>,
): ServerProviderSkill[] {
  const byCanonicalPath = new Map<string, ServerProviderSkill>();
  for (const skill of skills) {
    if (!skill.enabled || skill.usable === false || byCanonicalPath.has(skill.path)) continue;
    byCanonicalPath.set(skill.path, skill);
  }

  return [...byCanonicalPath.values()].sort((left, right) => {
    const nameComparison = left.name.localeCompare(right.name, undefined, { sensitivity: "base" });
    if (nameComparison !== 0) return nameComparison;
    const scopeComparison = providerSkillScopeRank(left) - providerSkillScopeRank(right);
    return scopeComparison !== 0 ? scopeComparison : left.path.localeCompare(right.path);
  });
}

export function scoreProviderSkill(skill: ServerProviderSkill, query: string): number | null {
  const normalizedName = skill.name.toLowerCase();
  const normalizedLabel = formatProviderSkillDisplayName(skill).toLowerCase();
  const normalizedShortDescription = skill.shortDescription?.toLowerCase() ?? "";
  const normalizedDescription = skill.description?.toLowerCase() ?? "";
  const normalizedScope = skill.scope?.toLowerCase() ?? "";

  const scores = [
    scoreQueryMatch({
      value: normalizedName,
      query,
      exactBase: 0,
      prefixBase: 2,
      boundaryBase: 4,
      includesBase: 6,
      fuzzyBase: 100,
      boundaryMarkers: ["-", "_", "/"],
    }),
    scoreQueryMatch({
      value: normalizedLabel,
      query,
      exactBase: 1,
      prefixBase: 3,
      boundaryBase: 5,
      includesBase: 7,
      fuzzyBase: 110,
    }),
    scoreQueryMatch({
      value: normalizedShortDescription,
      query,
      exactBase: 20,
      prefixBase: 22,
      boundaryBase: 24,
      includesBase: 26,
    }),
    scoreQueryMatch({
      value: normalizedDescription,
      query,
      exactBase: 30,
      prefixBase: 32,
      boundaryBase: 34,
      includesBase: 36,
    }),
    scoreQueryMatch({
      value: normalizedScope,
      query,
      exactBase: 40,
      prefixBase: 42,
      includesBase: 44,
    }),
  ].filter((score): score is number => score !== null);

  if (scores.length === 0) {
    return null;
  }

  return Math.min(...scores);
}

export function searchProviderSkills(
  skills: ReadonlyArray<ServerProviderSkill>,
  query: string,
  options?: {
    readonly limit?: number;
  },
): ServerProviderSkill[] {
  const enabledSkills = normalizeProviderSkillCatalog(filterUserFacingProviderSkills(skills));
  const normalizedQuery = normalizeSearchQuery(query, { trimLeadingPattern: /^\$+/ });
  const limit = options?.limit ?? Number.POSITIVE_INFINITY;

  if (!normalizedQuery) {
    return enabledSkills;
  }

  const ranked: Array<{
    item: ServerProviderSkill;
    score: number;
    tieBreaker: string;
  }> = [];

  for (const skill of enabledSkills) {
    const score = scoreProviderSkill(skill, normalizedQuery);
    if (score === null) {
      continue;
    }

    insertRankedSearchResult(
      ranked,
      {
        item: skill,
        score,
        tieBreaker: `${formatProviderSkillDisplayName(skill).toLowerCase()}\u0000${skill.name}`,
      },
      limit,
    );
  }

  return ranked.map((entry) => entry.item);
}
